import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Phone, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { trackFormSubmit } from '../../utils/analytics';
import { BACKEND_URL } from '../../lib/apiBase';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    privacy: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      trackFormSubmit('contact');
      toast.success('Üzenet elküldve! Hamarosan felvesszük Önnel a kapcsolatot.');
      setFormData({ name: '', email: '', phone: '', message: '', privacy: false });
    } catch (error) {
      toast.error('Hiba történt az üzenet küldésekor. Kérjük, próbálja újra.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kapcsolat" className="section-padding bg-[#404041] relative overflow-hidden" data-testid="contact-section">
      {/* Background */}
      <div className="absolute inset-0">
        <img loading="lazy"
          src="/contact-bg.avif"
          alt="Az Akadémia kapcsolati oldal — Budapest, Kapás utca"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/80" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16">
          {/* Header - mobile: 1st, desktop: left column top */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            className="order-1 lg:order-1"
          >
            <p className="text-[#FBAF3F] font-mono text-sm tracking-widest uppercase mb-4">
              Kapcsolat
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6">
              Beszéljünk a céljaidról
            </h2>
            <p className="text-white/60 text-lg mb-10 lg:mb-0">
              Akár egyéni fejlődésről, akár céges tréningről van szó — első lépésként
              egy rövid beszélgetéssel kezdünk. Írd meg, miben segíthetünk.
            </p>
          </motion.div>

          {/* Form - mobile: 2nd, desktop: right column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ delay: 0.2 }}
            className="order-2 lg:order-2 lg:row-span-3"
          >
            <form onSubmit={handleSubmit} className="glass-card p-5 md:p-8 lg:p-12" data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <input
                    type="text"
                    placeholder="Név *"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-underline"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Email cím *"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-underline"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Telefonszám"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-underline"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <textarea
                    placeholder="Üzenet *"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="input-underline resize-none"
                    data-testid="contact-input-message"
                  />
                </div>
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={formData.privacy}
                      onChange={(e) => setFormData({ ...formData, privacy: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-[#FBAF3F] flex-shrink-0"
                      data-testid="contact-input-privacy"
                    />
                    <span className="text-white/60 text-sm leading-relaxed">
                      Elolvastam és elfogadom az{' '}
                      <Link to="/adatvedelem" target="_blank" className="text-[#56999B] hover:text-[#C4DB72] underline transition-colors">
                        Adatkezelési tájékoztatót
                      </Link>
                      , és hozzájárulok személyes adataim kezeléséhez. *
                    </span>
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Küldés...' : 'Üzenet küldése'}
                  <Send size={18} />
                </button>
              </div>
            </form>
          </motion.div>

          {/* Contact details - mobile: 3rd, desktop: left column middle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ delay: 0.2 }}
            className="order-3 lg:order-3"
          >
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#07516B]/20 flex items-center justify-center">
                  <MapPin size={20} className="text-[#56999B]" />
                </div>
                <div>
                  <p className="text-white font-medium">Cím</p>
                  <p className="text-white/60">1027 Budapest, Kapás utca 6-12</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#07516B]/20 flex items-center justify-center">
                  <Phone size={20} className="text-[#56999B]" />
                </div>
                <div>
                  <p className="text-white font-medium">Telefon</p>
                  <a href="tel:+36705188091" className="text-white/60 hover:text-brand-light-green transition-colors block">
                    +36 70 518 8091
                  </a>
                  <a href="tel:+3615552222" className="text-white/60 hover:text-brand-light-green transition-colors block">
                    +36 (1) 555-2222
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#07516B]/20 flex items-center justify-center">
                  <Clock size={20} className="text-[#56999B]" />
                </div>
                <div>
                  <p className="text-white font-medium">Ügyfélfogadás</p>
                  <p className="text-white/60">H-CS: 09:00-17:30</p>
                  <p className="text-white/60">P: 09:00-16:00</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Map - mobile: 4th, desktop: left column bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ delay: 0.3 }}
            className="order-4 lg:order-4"
          >
            <div className="glass-card overflow-hidden" data-testid="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2695.1234567890123!2d19.0345678!3d47.5123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741dc14ca087e31%3A0x7895c0e5e9c5b0d0!2sKap%C3%A1s%20u.%206-12%2C%20Budapest%2C%201027%20Hungary!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="200"
                style={{ border: 0, filter: 'grayscale(50%) contrast(1.1)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Akadémia Plusz 2.0 Kft. - Térkép"
                className="w-full"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
